import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  CloseButton,
  Text,
} from '@chakra-ui/react';
import { FormValues, VisitData } from 'types';
import { Loader, SubmitButton, TermsPolicyLinks, VisitInfo } from 'components';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import useAxios from 'axios-hooks';
import { Rating, Textarea } from 'components/formComponents';
import { CheckCircleIcon } from 'static/icons';

const ContainerStyled = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: calc(10px + 2vmin);
  max-width: 787px;
  margin: auto;
  padding: 0 16px;
`;

const schema = Yup.object().shape({
  feedback: Yup.string().max(
    2000,
    'Превышен лимит текстового поля в 2000 символов',
  ),
  rating: Yup.number().min(
    1,
    'Для отправки отзыва необходимо поставить оценку',
  ),
});

const defaultValues: FormValues = {
  feedback: '',
  rating: 0,
};

export const MainPage: React.FC = () => {
  const { alias } = useParams<{ alias: string }>();

  const [{ data, loading, error }] = useAxios<VisitData>(
    {
      url: `/review/client/review/${alias}`,
    },
    { manual: !alias },
  );

  const [{ loading: formLoading, error: formError }, postFeedback] = useAxios(
    {
      method: 'POST',
      url: `/review/client/review`,
    },
    {
      manual: true,
    },
  );

  const [isShowError, setShowError] = useState<boolean>(true);
  const hideError = () => setShowError(false);
  const [isConfirmed, setConfirmed] = useState<boolean>();
  const isProvided = !!(error?.response?.status === 403);

  const onSubmit = ({ rating, feedback }: FormValues) => {
    return postFeedback({
      data: { alias, rating: String(rating), feedback },
    })
      .then(() => setConfirmed(true))
      .catch(() => setShowError(true));
  };

  return (
    <ContainerStyled>
      <Loader loading={loading} error={(!isProvided && error) || !alias} />
      {isConfirmed || isProvided ? (
        <>
          <CheckCircleIcon />
          <Text color="black" textStyle="h2" mt="6">
            {isProvided ? 'Отзыв уже предоставлен' : 'Спасибо за отзыв!'}
          </Text>
        </>
      ) : (
        <>
          <Text
            color="grayText"
            textStyle="h1"
            mt="46"
            mb={formError && isShowError ? undefined : '100'}
          >
            Оцените визит
          </Text>
          {formError && isShowError && (
            <Alert
              status="error"
              justifyContent="space-between"
              borderRadius={4}
              boxShadow="0 5px 5px -3px rgba(0, 0, 0, 0.02), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12)"
              maxW={400}
              mt={7}
              mb={4}
            >
              <AlertIcon />
              <AlertDescription color="black" textStyle="body2">
                Ошибка при отправке. Попробуйте позже
              </AlertDescription>
              <CloseButton onClick={hideError} />
            </Alert>
          )}
          <VisitInfo visit={data} />
          <Box width="100%" my={4}>
            <Formik
              initialValues={defaultValues}
              validationSchema={schema}
              onSubmit={onSubmit}
            >
              {({ handleSubmit }) => {
                return (
                  <Form onSubmit={handleSubmit}>
                    <Rating name="rating" />
                    <Textarea
                      name="feedback"
                      mt={4}
                      minHeight={{ base: '120px', sm: '210px' }}
                      placeholder="Поделитесь впечатлениями о визите"
                    />
                    <SubmitButton submitting={formLoading} />
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </>
      )}
    </ContainerStyled>
  );
};
